import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Flex, Table, theme } from 'antd';
import type {
  CategoryListOut,
  CollectionOut,
  ProfileOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';

const FavoriteCategoriesCard = ({ ids }: { ids: number[] }) => {
  const t = useTranslate();
  const hasIds = ids.length > 0;
  const { tableProps, result } = useTable<CategoryListOut>({
    resource: 'user_catalog.categories',
    queryOptions: { enabled: hasIds },
    filters: {
      permanent: [{ field: 'id', operator: 'in', value: ids }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card
      title={`${t('account.profiles.favorites.categories_section')} (${result?.total ?? ids.length})`}
    >
      {/* Disabled query never becomes `isFetched`, so refine's
          `tableProps.loading` (= !isFetched) would spin forever. */}
      <Table<CategoryListOut>
        {...tableProps}
        loading={hasIds ? tableProps.loading : false}
        dataSource={hasIds ? tableProps.dataSource : []}
        rowKey="id"
        size="small"
      >
        <Table.Column<CategoryListOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={80}
          render={(value: number) => (
            <ShowIconLink resource="user_catalog.categories" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<CategoryListOut>
          dataIndex="name"
          title={t('user_catalog.categories.fields.name')}
        />
        <Table.Column<CategoryListOut>
          dataIndex="collection_kind"
          title={t('user_catalog.categories.fields.collection_kind')}
          render={(value: string) =>
            value ? t(`user_catalog.categories.collection_kind.${value}`) : '-'
          }
        />
      </Table>
    </Card>
  );
};

const FavoriteCollectionsCard = ({ ids }: { ids: number[] }) => {
  const t = useTranslate();
  const hasIds = ids.length > 0;
  const { tableProps, result } = useTable<CollectionOut>({
    resource: 'user_catalog.collections',
    queryOptions: { enabled: hasIds },
    filters: {
      permanent: [{ field: 'id', operator: 'in', value: ids }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card
      title={`${t('account.profiles.favorites.collections_section')} (${result?.total ?? ids.length})`}
    >
      <Table<CollectionOut>
        {...tableProps}
        loading={hasIds ? tableProps.loading : false}
        dataSource={hasIds ? tableProps.dataSource : []}
        rowKey="id"
        size="small"
      >
        <Table.Column<CollectionOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={80}
          render={(value: number) => (
            <ShowIconLink resource="user_catalog.collections" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<CollectionOut>
          dataIndex="name"
          title={t('user_catalog.collections.fields.name')}
        />
        <Table.Column<CollectionOut>
          dataIndex="collection_kind"
          title={t('user_catalog.collections.fields.collection_kind')}
          render={(value: string) =>
            value ? t(`user_catalog.collections.kind.${value}`) : '-'
          }
        />
      </Table>
    </Card>
  );
};

export const ProfileFavoritesTab = ({ record }: { record: ProfileOut }) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={token.margin}>
      <FavoriteCategoriesCard ids={record.favorite_category_ids ?? []} />
      <FavoriteCollectionsCard ids={record.favorite_collection_ids ?? []} />
    </Flex>
  );
};
